import { createSlice } from '@reduxjs/toolkit'
import { Auth } from 'aws-amplify'
import axios from 'axios'
import api, { createWrappedAsyncThunk } from 'src/api'
import config from '../../../../config'

export const initialState = {
  sections: [],
  jobTypes: [],
  ingredient: null,
  ingredients: [],
  jobType: null,
  equipmentType: null,
  equipmentTypes: [],
  recipes: [],
  recipe: null,
  recipeIngredients: [],
  categories: [],
  category: null,
  searchIngredientsAndRecipesResults: [],
  isLoading: false,
  menuFilterChoices: [],
  categoryFilterChoices: [],
  agreedSuppliers: [],
  agreementProducts: [],
  fromRecipeId: null,
  search: '',
}

const SLICE_NAME = 'buyer/recipes'

const getJwt = async () => {
  const session = await Auth.currentSession()
  return session.getIdToken().getJwtToken()
}

export const fetchJobTypes = createWrappedAsyncThunk(
  `${SLICE_NAME}/fetchJobTypes`,
  purchasingEnvironment => api.get('/buyer/job-types/')
)

export const fetchEquipmentTypes = createWrappedAsyncThunk(
  `${SLICE_NAME}/fetchEquipmentTypes`,
  purchasingEnvironment => api.get('/buyer/equipments/')
)

export const createIngredient = createWrappedAsyncThunk(
  `${SLICE_NAME}/createIngredient`,
  body => api.post('/buyer/ingredients/', { body })
)

export const updateIngredient = createWrappedAsyncThunk(
  `${SLICE_NAME}/updateIngredient`,
  args => api.patch(`/buyer/ingredients/${args.id}/`, { body: args.body })
)

export const fetchIngredients = createWrappedAsyncThunk(
  `${SLICE_NAME}/fetchIngredients`,
  // TODO: Currently the system doesn't appear to call on limits - fix this
  args => api.get('/buyer/ingredients/?limit=1000')
)

export const fetchIngredientById = createWrappedAsyncThunk(
  `${SLICE_NAME}/fetchIngredientsById`,
  id => api.get(`/buyer/ingredients/${id}`)
)

export const createJobType = createWrappedAsyncThunk(
  `${SLICE_NAME}/createJobType`,
  body => api.post('/buyer/job-types/', { body })
)

export const updateJobType = createWrappedAsyncThunk(
  `${SLICE_NAME}/updateJobType`,
  args => api.patch(`/buyer/job-types/${args.id}/`, { body: args.body })
)

export const createEquipmentType = createWrappedAsyncThunk(
  `${SLICE_NAME}/createEquipmentType`,
  body => api.post('/buyer/equipments/', { body })
)

export const updateEquipmentType = createWrappedAsyncThunk(
  `${SLICE_NAME}/updateEquipmentType`,
  args => api.patch(`/buyer/equipments/${args.id}/`, { body: args.body })
)

export const fetchRecipes = createWrappedAsyncThunk(
  `${SLICE_NAME}/fetchRecipes`,
  args => {
    const isOnMenu =
      args.menuFilterChoices && args.menuFilterChoices.includes('onMenu')

    const categoryQs =
      args.categoryFilterChoices &&
      args.categoryFilterChoices.length > 0 &&
      args.categoryFilterChoices
        .map(categoryId => `category=${categoryId}`)
        .join('&')

    const searchQs = args.search ? `search=${args.search}` : ''

    // Build the query string dynamically
    const queryStringParts = [
      `environment_id=${args.purchasingEnvironment}`,
      isOnMenu && 'is_on_menu=true',
      categoryQs,
      searchQs,
    ].filter(part => part) // Remove any empty or falsy parts

    const queryString = queryStringParts.join('&')

    return api.get(`/buyer/recipes/?${queryString}&limit=1000`)
  }
)

export const fetchRecipeById = createWrappedAsyncThunk(
  `${SLICE_NAME}/fetchRecipeById`,
  async args => {
    console.log(
      `/buyer/recipes/${args.id}/?environment_id=${args.purchasingEnvironment}`
    )
    const recipe = await api.get(
      `/buyer/recipes/${args.id}/?environment_id=${args.purchasingEnvironment}`
    )

    // FIXME: Annoying hoop jump to get the price of
    // FIXME: components since it's unsupported in the API
    // const components = recipe.recipeIngredients.filter(
    //   i => i.ingredient.recipeType === 'recipe_component'
    // )
    // const fullComponents = await Promise.all(
    //   components.map(async component => {
    //     const componentRecipe = await api.get(
    //       `/buyer/recipes/${component.ingredient.id}/?environment_id=${args.purchasingEnvironment}`
    //     )

    //     return componentRecipe
    //   })
    // )
    // recipe.recipeIngredients = recipe.recipeIngredients.map(ingredient => {
    //   const component = fullComponents.find(
    //     c => c.id === ingredient.ingredient.id
    //   )
    //   if (component) {
    //     ingredient.ingredient = component
    //   }
    //   return ingredient
    // })
    // FIXME: End of hoop jump

    return recipe
  }
)

export const fetchRecipeIngredients = createWrappedAsyncThunk(
  `${SLICE_NAME}/fetchRecipeIngredients`,
  body => api.get('/buyer/recipe-ingredients/')
)

export const createCategory = createWrappedAsyncThunk(
  `${SLICE_NAME}/createCategory`,
  body => api.post('/buyer/categories/', { body })
)

export const updateCategory = createWrappedAsyncThunk(
  `${SLICE_NAME}/updateCategory`,
  args => api.patch(`/buyer/categories/${args.id}/`, { body: args.body })
)

export const fetchCategories = createWrappedAsyncThunk(
  `${SLICE_NAME}/fetchCategories`,
  purchasingEnvironment => api.get('/buyer/categories/')
)

export const searchIngredientsAndRecipes = createWrappedAsyncThunk(
  `${SLICE_NAME}/searchIngredientsAndRecipes`,
  term => api.get(`/buyer/search-recipes-and-ingredients/?name=${term}`)
)

export const fetchAgreedSuppliers = createWrappedAsyncThunk(
  `${SLICE_NAME}/fetchAgreedSuppliers`,
  term => api.get(`/buyer/agreed-suppliers/`)
)

export const fetchAgreementProducts = createWrappedAsyncThunk(
  `${SLICE_NAME}/fetchAgreementProducts`,
  id => api.get(`/buyer/agreement-products/?supplier_id=${id}`)
)

export const createBaseRecipe = createWrappedAsyncThunk(
  `${SLICE_NAME}/createBaseRecipe`,
  body => api.post('/buyer/recipes/', { body })
)

export const updateBaseRecipe = createWrappedAsyncThunk(
  `${SLICE_NAME}/updateBaseRecipe`,
  args => {
    return api.patch(`/buyer/recipes/${args.id}/`, { body: args.body })
  }
)

export const createRecipeIngredient = createWrappedAsyncThunk(
  `${SLICE_NAME}/createRecipeIngredient`,
  body => api.post('/buyer/recipe-ingredients/', { body })
)

export const updateRecipeIngredient = createWrappedAsyncThunk(
  `${SLICE_NAME}/updateRecipeIngredient`,
  args => {
    api.patch(`/buyer/recipe-ingredients/${args.id}/`, { body: args.body })
  }
)

export const deleteRecipeIngredient = createWrappedAsyncThunk(
  `${SLICE_NAME}/deleteRecipeIngredient`,
  recipeIngredientId => {
    api.del(`/buyer/recipe-ingredients/${recipeIngredientId}/`)
  }
)

export const createRecipeStep = createWrappedAsyncThunk(
  `${SLICE_NAME}/createRecipeStep`,
  body => api.post('/buyer/recipe-steps/', { body })
)

export const createRecipeStepJobType = createWrappedAsyncThunk(
  `${SLICE_NAME}/createRecipeStepJobType`,
  body => api.post('/buyer/recipe-step-job-types/', { body })
)

export const createRecipeStepEquipmentType = createWrappedAsyncThunk(
  `${SLICE_NAME}/createRecipeStepEquipmentType`,
  body => api.post('/buyer/recipe-step-equipments/', { body })
)

export const updateRecipeStep = createWrappedAsyncThunk(
  `${SLICE_NAME}/updateRecipeStep`,
  args => api.patch(`/buyer/recipe-steps/${args.id}/`, { body: args.body })
)

export const updateRecipeStepJobType = createWrappedAsyncThunk(
  `${SLICE_NAME}/updateRecipeStepJobType`,
  args =>
    api.post(`/buyer/recipe-step-job-types/${args.id}`, { body: args.body })
)

export const updateRecipeStepEquipmentType = createWrappedAsyncThunk(
  `${SLICE_NAME}/updateRecipeStepEquipmentType`,
  args =>
    api.post(`/buyer/recipe-step-equipments/${args.id}/`, { body: args.body })
)

export const addToEnvironment = createWrappedAsyncThunk(
  `${SLICE_NAME}/addToEnvironment`,
  args =>
    api.post('/buyer/recipe-environments/', {
      body: {
        recipe: args.recipeId,
        environment: args.environmentId,
      },
    })
)

export const removeFromEnvironment = createWrappedAsyncThunk(
  `${SLICE_NAME}/removeFromEnvironment`,
  // id in this case is recipeEnvironmentRelationshipId or currentRecipeEnvironmentId
  id => api.del(`/buyer/recipe-environments/${id}`)
)

export const createRecipePhoto = createWrappedAsyncThunk(
  `${SLICE_NAME}/createRecipePhoto`,
  async args => {
    try {
      const jwt = await getJwt()
      if (jwt) {
        const result = await axios.patch(
          `${config.api.endpoint}/buyer/recipes/${args.recipeId}/photo/`,
          args.formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: `Bearer ${jwt}`,
            },
          }
        )
        return result.data
      }
    } catch (e) {
      return e
    }
  }
)

export const createStepPhoto = createWrappedAsyncThunk(
  `${SLICE_NAME}/createStepPhoto`,
  async args => {
    try {
      const jwt = await getJwt()
      if (jwt) {
        const result = await axios.patch(
          `${config.api.endpoint}/buyer/recipe-steps/${args.stepId}/photo/`,
          args.formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: `Bearer ${jwt}`,
            },
          }
        )
        return result.data
      }
    } catch (e) {
      return e
    }
  }
)

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setMenuFilters(state, { payload }) {
      state.menuFilterChoices = payload
      return state
    },
    setCategoryFilters(state, { payload }) {
      state.categoryFilterChoices = payload
      return state
    },
    setFromRecipeId(state, { payload }) {
      state.fromRecipeId = payload
      return state
    },
    resetFromRecipeId(state, { payload }) {
      state.fromRecipeId = null
      return state
    },
    setSearch(state, { payload }) {
      state.search = payload
      return state
    },
  },
  extraReducers: {
    [fetchJobTypes.fulfilled]: (state, { payload }) => {
      state.jobTypes = payload.results
      state.isLoading = false
      return state
    },
    [fetchJobTypes.pending]: (state, { payload }) => {
      state.isLoading = true
      return state
    },
    [createIngredient.fulfilled]: (state, { payload }) => {
      state.ingredient = payload.results
      return state
    },
    [updateIngredient.fulfilled]: (state, { payload }) => {
      state.ingredient = payload.results
      return state
    },
    [fetchIngredients.fulfilled]: (state, { payload }) => {
      state.ingredients = payload.results
      state.isLoading = false
      return state
    },
    [fetchIngredients.pending]: (state, { payload }) => {
      state.isLoading = true
      state.agreementProducts = null
      state.ingredient = null
      return state
    },
    [fetchIngredientById.fulfilled]: (state, { payload }) => {
      state.ingredient = payload
      state.isLoading = false
      return state
    },
    [fetchIngredientById.pending]: (state, { payload }) => {
      state.ingredient = null
      state.isLoading = true
      return state
    },
    [fetchEquipmentTypes.fulfilled]: (state, { payload }) => {
      state.equipmentTypes = payload.results
      state.isLoading = false
      return state
    },
    [fetchEquipmentTypes.pending]: (state, { payload }) => {
      state.isLoading = true
      return state
    },
    [createJobType.fulfilled]: (state, { payload }) => {
      state.jobType = payload
      // TODO: not ideal but works for now
      state.jobTypes = [...state.jobTypes, payload]
      return state
    },
    // TODO: Fix This
    [updateJobType.fulfilled]: (state, { payload }) => {
      state.jobType = payload
      // Find the index of the updated job type in the jobTypes array
      const updatedIndex = state.jobTypes.findIndex(
        jobType => jobType.id === payload.id
      )
      // Update the job type at the updatedIndex with the new payload value
      state.jobTypes = state.jobTypes.map((jobType, index) =>
        index === updatedIndex ? payload : jobType
      )
      return state
    },
    [createEquipmentType.fulfilled]: (state, { payload }) => {
      state.equipmentType = payload
      // TODO: not ideal but works for now
      state.equipmentTypes = [...state.equipmentTypes, payload]
      return state
    },
    [updateEquipmentType.fulfilled]: (state, { payload }) => {
      state.equipmentType = payload
      const updatedIndex = state.equipmentTypes.findIndex(
        equipmentType => equipmentType.id === payload.id
      )
      state.equipmentTypes = state.equipmentTypes.map((equipmentType, index) =>
        index === updatedIndex ? payload : equipmentType
      )
      return state
    },
    [fetchRecipes.fulfilled]: (state, { payload }) => {
      state.recipes = payload ?? []
      return state
    },
    [fetchRecipeById.fulfilled]: (state, { payload }) => {
      state.recipe = payload ?? []
      state.isLoading = false
      return state
    },
    [fetchRecipeById.pending]: (state, { payload }) => {
      state.recipe = null
      state.isLoading = true
      return state
    },
    [fetchRecipeIngredients.fulfilled]: (state, { payload }) => {
      state.recipeIngredients = payload.results ?? []
      return state
    },
    [fetchCategories.fulfilled]: (state, { payload }) => {
      state.categories = payload.results ?? []
      state.isLoading = false
      return state
    },
    [fetchCategories.pending]: (state, { payload }) => {
      state.isLoading = true
      return state
    },
    [searchIngredientsAndRecipes.fulfilled]: (state, { payload }) => {
      state.searchIngredientsAndRecipesResults = payload.data ?? []
      state.isLoading = false
      return state
    },
    [searchIngredientsAndRecipes.pending]: (state, { payload }) => {
      state.isLoading = true
      return state
    },
    [createCategory.fulfilled]: (state, { payload }) => {
      state.category = payload
      // TODO: not ideal but works for now
      state.categories = [...state.categories, payload]
      return state
    },
    [updateCategory.fulfilled]: (state, { payload }) => {
      state.category = payload
      const updatedIndex = state.categories.findIndex(
        category => category.id === payload.id
      )
      state.categories = state.categories.map((category, index) =>
        index === updatedIndex ? payload : category
      )
      return state
    },
    [createBaseRecipe.fulfilled]: (state, { payload }) => {
      state.recipe = payload
      return state
    },
    [updateBaseRecipe.fulfilled]: (state, { payload }) => {
      state.recipe = payload
      return state
    },
    [createRecipeIngredient.fulfilled]: (state, { payload }) => {
      return state
    },
    [updateRecipeIngredient.fulfilled]: (state, { payload }) => {
      return state
    },
    [createRecipeStepJobType.fulfilled]: (state, { payload }) => {
      return state
    },
    [createRecipeStepEquipmentType.fulfilled]: (state, { payload }) => {
      return state
    },
    [createRecipeStep.fulfilled]: (state, { payload }) => {
      return state
    },
    [updateRecipeStepJobType.fulfilled]: (state, { payload }) => {
      return state
    },
    [updateRecipeStepEquipmentType.fulfilled]: (state, { payload }) => {
      return state
    },
    [updateRecipeStep.fulfilled]: (state, { payload }) => {
      return state
    },
    [addToEnvironment.fulfilled]: (state, { payload }) => {
      return state
    },
    [removeFromEnvironment.fulfilled]: (state, { payload }) => {
      return state
    },
    [createRecipePhoto.fulfilled]: (state, { payload }) => {
      return state
    },
    [createStepPhoto.fulfilled]: (state, { payload }) => {
      return state
    },
    [fetchAgreedSuppliers.fulfilled]: (state, { payload }) => {
      state.agreedSuppliers = payload.results ?? []
      return state
    },
    [fetchAgreementProducts.fulfilled]: (state, { payload }) => {
      state.agreementProducts = payload ?? []
      return state
    },
  },
})

export const {
  setMenuFilters,
  setCategoryFilters,
  setFromRecipeId,
  resetFromRecipeId,
  setSearch,
} = slice.actions

export default slice.reducer
